import { JSX } from 'react';
import { DeepPartial, UseFormReturn } from 'react-hook-form';

import { AIFilledHintDecoration } from 'components/AIHint';
import RequiredHint from 'components/RequiredHint';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { useExtendedFormContext } from 'contexts/extendedFormContext';

import { PLS_FIELD_LIMITS } from './constants';
import { PlsFormValues, PlsOperationalFieldDefinition } from './types';

type OperationalFieldsSectionProps = {
  formMethods: UseFormReturn<PlsFormValues>;
  fieldDefinitions: PlsOperationalFieldDefinition[];
  isLoading?: boolean;
  error?: unknown;
  // Building a load: PLS requires every customer reference field the bill-to has, so each is
  // marked required and Build Load checks them (validatePlsRequiredCustomerReferences).
  isCreateMode?: boolean;
};

export function OperationalFieldsSection({
  formMethods,
  fieldDefinitions,
  isLoading = false,
  error,
  isCreateMode = false,
}: OperationalFieldsSectionProps): JSX.Element {
  const {
    watch,
    formState: { defaultValues },
  } = formMethods;
  const { aiDefaultValues } = useExtendedFormContext();
  const watchedBillToId = watch('billTo.externalTMSID');
  const values = watch('plsOperationalFields') ?? {};

  if (!watchedBillToId) {
    return (
      <Typography variant='body-sm' className='text-neutral-600'>
        Select a bill-to to load customer reference fields.
      </Typography>
    );
  }

  if (isLoading) {
    return (
      <Typography variant='body-sm' className='text-neutral-600'>
        Loading customer reference fields...
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography variant='body-sm' className='text-error-600'>
        Customer reference fields could not be loaded. Please retry.
      </Typography>
    );
  }

  if (fieldDefinitions.length === 0) {
    return (
      <Typography variant='body-sm' className='text-neutral-600'>
        No customer reference fields configured for this bill-to.
      </Typography>
    );
  }

  return (
    <Flex direction='col' gap='md'>
      {fieldDefinitions.map((fieldDef) => {
        const isAIFilled =
          aiDefaultValues &&
          isSuggestedOperationalValue(
            defaultValues,
            fieldDef.code,
            values[fieldDef.code]
          );

        return (
          <div key={fieldDef.code}>
            <RHFTextInput
              name={`plsOperationalFields.${fieldDef.code}`}
              label={fieldDef.name}
              placeholder={`Enter ${fieldDef.name.toLowerCase()}`}
              maxLength={PLS_FIELD_LIMITS.operationalFieldValue}
              // The suggestion files these under Load Reference Fields and the form moves them
              // here once the bill-to's fields load, so the form's own AI hint never sees a
              // default; isSuggestedOperationalValue does.
              hideAIHint
              // Required is marked, not a form rule: Build Load names the empty field in its
              // own message (validatePlsRequiredCustomerReferences), and a rule would outlive a
              // bill-to switch that drops the field.
              labelAdornment={
                <>
                  {isCreateMode && <RequiredHint />}
                  {isAIFilled && <AIFilledHintDecoration />}
                </>
              }
            />
          </div>
        );
      })}
    </Flex>
  );
}

// True when the value is what the suggestion put in this field, under either map (see above).
// Codes match without case, as PLS's do.
export function isSuggestedOperationalValue(
  defaultValues: DeepPartial<PlsFormValues> | undefined,
  code: string,
  value: string | undefined
): boolean {
  const current = value?.trim();
  if (!current) {
    return false;
  }
  const key = code.trim().toUpperCase();
  return [
    ...Object.entries(defaultValues?.plsOperationalFields ?? {}),
    ...Object.entries(defaultValues?.plsLoadReferenceFields ?? {}),
  ].some(
    ([fieldCode, fieldValue]) =>
      fieldCode.trim().toUpperCase() === key &&
      String(fieldValue ?? '').trim() === current
  );
}
